/**
 * overlayEscStack — 全局弹层 ESC 栈（后进先关）
 *
 * 解决全仓 89/111 弹层文件无 ESC 关闭、且各层自监听导致"叠层时按 Esc 多层同关/不关"的乱象。
 *
 * 设计要点：
 * - 监听挂 window 捕获阶段：捕获自 window 向下传播，先于一切 document/target 监听执行，
 *   栈顶处理后 stopPropagation 即可阻断存量组件级 Escape 监听，杜绝双关。
 * - 输入场景（input/textarea/select/contenteditable）完全透传：组件自有的输入框 ESC
 *   语义（命令面板/保存弹窗等）原样保留；入栈面板在输入聚焦时按 ESC 不误关。
 * - 仅栈非空时拦截事件；栈空时零干预，不影响浏览器/输入法原生 Esc 行为。
 */

interface OverlayEntry {
    id: symbol;
    label: string;
    close: () => void;
}

const stack: OverlayEntry[] = [];
let installed = false;

function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    const top = stack[stack.length - 1];
    if (!top) return;
    // 输入场景（input/textarea/select/contenteditable 聚焦）完全透传、零干预：
    // 未入栈组件自有的输入框 ESC 语义（如命令面板 ESC 关闭、保存弹窗输入框 esc 关窗）
    // 必须原样保留，栈不越权；入栈面板在输入聚焦时按 ESC 亦不误关（先点层外再 Esc）。
    if (isTypingTarget(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    // 先同步出栈再关闭：不依赖组件侧 watcher 异步 unregister，
    // 保证快速连按 ESC 时逐层精确后进先关（组件侧 unregister 幂等兜底）
    stack.pop();
    top.close();
}

/** 安装全局监听（幂等）。App 启动时调用一次。 */
export function installGlobalEscStack(): void {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    window.addEventListener('keydown', onKeydown, true);
}

/** 卸载（仅测试用）。 */
export function uninstallGlobalEscStack(): void {
    if (!installed) return;
    installed = false;
    stack.length = 0;
    window.removeEventListener('keydown', onKeydown, true);
}

/**
 * 弹层入栈。返回 unregister——**必须在弹层关闭与组件卸载两条路径都调用**
 * （重复调用安全，内部按 id 幂等移除）。
 */
export function pushOverlay(close: () => void, label = 'overlay'): () => void {
    const entry: OverlayEntry = { id: Symbol(`esc:${label}`), label, close };
    stack.push(entry);
    return () => {
        const i = stack.indexOf(entry);
        if (i >= 0) stack.splice(i, 1);
    };
}

/** 当前栈深（调试/测试）。 */
export function escStackDepth(): number {
    return stack.length;
}
